import  ApiHelper from '../../ApiHelper';
import API_ENDPOINTS  from '../../endpoints';

/**
 * User profile management API requests
 */
class ProfileHelper {
    /**
     * Get current user profile
     * @returns {Promise<Object>} User profile data
     */
    async getProfile() {
        try {
            const response = await ApiHelper.get(API_ENDPOINTS.AUTH.PROFILE);
            
            // Update session storage with latest profile data
            if (response.success && response.data) {
                sessionStorage.setItem('user', JSON.stringify(response.data));
            }

            return response;
        } catch (error) {
            throw new Error(`Failed to fetch profile: ${error.message}`);
        }
    }

    /**
     * Update user profile
     * @param {Object} profileData - Profile data to update
     * @returns {Promise<Object>} Updated profile response
     */
    async updateProfile(profileData) {
        try {
            const validatedData = this.validateProfileData(profileData);
            
            const response = await ApiHelper.post(
                API_ENDPOINTS.AUTH.PROFILE,
                validatedData
            );

            // Update session storage with new profile data
            if (response.success && response.data) {
                sessionStorage.setItem('user', JSON.stringify(response.data));
            }

            return response;
        } catch (error) {
            throw new Error(`Profile update failed: ${error.message}`);
        }
    }

    /**
     * Upload profile avatar
     * @param {File} avatarFile - Image file for avatar
     * @param {Function} onProgress - Upload progress callback
     * @returns {Promise<Object>} Upload response
     */
    async uploadAvatar(avatarFile, onProgress = null) {
        try {
            // Validate file type and size
            this.validateAvatarFile(avatarFile);

            const formData = new FormData();
            formData.append('avatar', avatarFile);

            const response = await ApiHelper.upload(
                API_ENDPOINTS.USERS.UPLOAD_AVATAR,
                formData,
                onProgress
            );

            return response;
        } catch (error) {
            throw new Error(`Avatar upload failed: ${error.message}`);
        }
    }

    /**
     * Get user preferences
     * @returns {Promise<Object>} User preferences
     */
    async getPreferences() {
        try {
            return await ApiHelper.get('/users/preferences');
        } catch (error) {
            throw new Error(`Failed to fetch preferences: ${error.message}`);
        }
    }

    /**
     * Update user preferences
     * @param {Object} preferences - User preferences
     * @returns {Promise<Object>} Updated preferences
     */
    async updatePreferences(preferences) {
        try {
            const response = await ApiHelper.post('/users/preferences', preferences);
            
            // Store preferences locally for quick access
            localStorage.setItem('userPreferences', JSON.stringify(preferences));
            
            return response;
        } catch (error) {
            throw new Error(`Failed to update preferences: ${error.message}`);
        }
    }

    /**
     * Validate profile data
     * @param {Object} profileData - Profile data to validate
     * @returns {Object} Validated profile data
     * @private
     */
    validateProfileData(profileData) {
        const allowedFields = [
            'first_name', 'last_name', 'phone', 'date_of_birth',
            'gender', 'address', 'bio'
        ];

        const validatedData = {};

        // Only include allowed fields
        Object.keys(profileData).forEach(key => {
            if (allowedFields.includes(key) && profileData[key] !== undefined) {
                validatedData[key] = profileData[key];
            }
        });

        // Validate specific fields
        if (validatedData.phone) {
            const phoneRegex = /^[6-9]\d{9}$/; // Indian phone number format
            if (!phoneRegex.test(validatedData.phone)) {
                throw new Error('Invalid phone number format');
            }
        }

        if (validatedData.first_name) {
            validatedData.first_name = validatedData.first_name.trim();
            if (validatedData.first_name.length < 2) {
                throw new Error('First name must be at least 2 characters long');
            }
        }

        if (validatedData.last_name) {
            validatedData.last_name = validatedData.last_name.trim();
            if (validatedData.last_name.length < 2) {
                throw new Error('Last name must be at least 2 characters long');
            }
        }

        return validatedData;
    }

    /**
     * Validate avatar file
     * @param {File} file - Avatar file to validate
     * @throws {Error} If file is invalid
     * @private
     */
    validateAvatarFile(file) {
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        const maxSize = 5 * 1024 * 1024; // 5MB

        if (!allowedTypes.includes(file.type)) {
            throw new Error('Only JPEG, PNG, and WebP images are allowed');
        }

        if (file.size > maxSize) {
            throw new Error('File size must be less than 5MB');
        }
    }

    /**
     * Get cached user data from session storage
     * @returns {Object|null} Cached user data
     */
    getCachedProfile() {
        try {
            const userData = sessionStorage.getItem('user');
            return userData ? JSON.parse(userData) : null;
        } catch (error) {
            console.error('Error parsing cached profile:', error);
            return null;
        }
    }
}

export default new ProfileHelper();
